import { Stack, Typography } from '@mui/material';
import PaiAutoComplete, { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { CompanyInformationForm } from './account-management.types';
import CompanyApiKeys from './company-api-keys';

interface Props {
  company: string;
  street: string;
  zip: string;
  city: string;
  countryOptions: OptionType[];
  country: OptionType | null;
  companyNameInvalid: boolean;
  cityInvalid: boolean;
  companyInfoLoading: boolean;

  handleCompanyInfoChange(field: keyof CompanyInformationForm): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleCountryChange(event: React.ChangeEvent<any>, newValue: OptionType): void;

  saveOwnCompanyChanges(): void;
}

const ManageCompany: React.FC<Props> = ({
  company,
  street,
  zip,
  city,
  countryOptions,
  country,
  companyNameInvalid,
  cityInvalid,
  companyInfoLoading,
  handleCompanyInfoChange,
  handleCountryChange,
  saveOwnCompanyChanges,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <div className={classes.manageCompany}>
      <div className={classes.settingsBox}>
        <div className={classes.boxDescription}>
          <Typography className={classes.boxTitle}>{t('companyDataTitle')}</Typography>
          <Typography className={classes.boxHint}>{t('companyDataHint')}</Typography>
        </div>
        <div className={classes.boxFields}>
          <Stack gap={2.5}>
            <PaiTextField
              label={t('companyName')}
              placeholder={t('enterCompanyNameHere')}
              value={company}
              onChange={handleCompanyInfoChange('company')}
              error={companyNameInvalid}
              errorText={companyNameInvalid ? t('companyNameTooLong') : undefined}
            />
            <Stack direction={'row'} gap={2}>
              <PaiTextField label={t('zip')} placeholder={t('enterZipHere')} value={zip} onChange={handleCompanyInfoChange('zip')} />
              <PaiTextField
                label={t('city')}
                placeholder={t('enterCityHere')}
                value={city}
                onChange={handleCompanyInfoChange('city')}
                error={cityInvalid}
                errorText={cityInvalid ? t('cityNameLength') : undefined}
              />
            </Stack>
            <PaiTextField label={t('street')} placeholder={t('enterStreetHere')} value={street} onChange={handleCompanyInfoChange('street')} />
            <PaiAutoComplete
              label={t('country')}
              placeholder={t('selectCompanyCountryHere')}
              selected={country}
              onChange={handleCountryChange}
              options={countryOptions}
            />
            <Stack className={classes.boxFooter} direction={'row'} justifyContent={'flex-end'}>
              <PaiButton
                text={t('save')}
                border={'rounded'}
                size={'small'}
                action={saveOwnCompanyChanges}
                disabled={
                  companyNameInvalid ||
                  company.trim().length === 0 ||
                  street.trim().length === 0 ||
                  zip.trim().length === 0 ||
                  city.trim().length === 0 ||
                  !country
                }
                loading={companyInfoLoading}
              />
            </Stack>
          </Stack>
        </div>
      </div>
      <CompanyApiKeys />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  manageCompany: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    height: '100%',
    overflow: 'auto',
  },
  settingsBox: {
    background: '#FFF',
    borderRadius: '12px',
    padding: '28px 32px',
    display: 'flex',
    gap: '40px',
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
      gap: '20px',
      padding: '24px',
    },
  },
  boxDescription: {
    width: '240px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  boxTitle: {
    fontSize: theme.typography.pxToRem(17),
    fontWeight: 500,
  },
  boxHint: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    textWrap: 'pretty',
  },
  boxFields: {
    flex: 1,
    minWidth: 0,
    maxWidth: '640px',
    // Field styling per design: 13px grey label above a 44px field with a neutral border.
    '& .MuiTypography-root:first-of-type': {
      fontSize: theme.typography.pxToRem(13),
      color: theme.palette.greys.medium,
      marginLeft: 0,
      marginBottom: '6px',
    },
    '& .MuiTextField-root': {
      borderRadius: '8px',
    },
    // Keep the error border of the base component untouched, only restyle the resting state.
    '& .MuiTextField-root:has(.MuiInputBase-root:not(.Mui-error))': {
      border: `1px solid ${theme.palette.greys.lighter}`,
    },
    '& .MuiTextField-root:has(.MuiInputBase-root.Mui-focused:not(.Mui-error))': {
      borderColor: theme.palette.primary.main,
    },
    '& .MuiInputBase-input': {
      borderRadius: '8px',
      minHeight: '26px',
      padding: '8px 14px',
    },
  },
  boxFooter: {
    paddingTop: '4px',
    '& .MuiButtonBase-root': {
      height: '40px',
      borderRadius: '8px',
      padding: '0 24px',
      fontSize: theme.typography.pxToRem(15),
      fontWeight: 700,
    },
  },
}));

export default ManageCompany;
